<script lang="ts" setup>
import { Button } from '@forgd/ui-kit/button'
import { useMounted } from '@vueuse/core'

const props = defineProps<{
  title?: string
  icon?: string
  description?: string
}>()

const auth = useAuth()
const { open: openLoginModal } = useLoginModal()

// Render name-less greeting on the server and first client paint, then fill in
// the name — avoids a hydration mismatch when auth resolves only on the client.
const mounted = useMounted()
const greeting = computed(() =>
  mounted.value && auth.isLoggedIn && auth.me?.firstName
    ? `Hi ${auth.me.firstName}, Welcome to Forgd`
    : 'Welcome to Forgd',
)

// Bound via `:src` (not a static `src`) so this core-layer component doesn't
// pull the app's public asset into other layer builds (e.g. image-maker).
const homeIcon = '/icons/home.svg'
</script>

<template>
  <div class="flex min-w-0 flex-1 items-center justify-between gap-4">
    <div class="flex min-w-0 items-center gap-2">
      <slot name="title-prefix" />
      <!-- Fall back to the home icon only for the icon-less greeting header; a titled
           page that omits an icon (e.g. marketing pages) renders no icon at all. -->
      <img v-if="props.icon || !props.title" :src="props.icon ?? homeIcon" alt="" class="size-8 shrink-0">
      <div v-if="props.title" class="flex min-w-0 flex-col justify-center">
        <span v-if="props.description" class="text-xs font-normal leading-none text-secondary">{{ props.description }}</span>
        <span class="truncate text-sm font-semibold text-primal">{{ props.title }}</span>
      </div>
      <span v-else class="truncate text-sm font-semibold text-primal">{{ greeting }}</span>
      <slot name="title-suffix" />
    </div>

    <ClientOnly>
      <CoreSearchButton class="ml-auto shrink-0 lg:absolute lg:left-1/2 lg:-translate-x-1/2" />

      <slot name="auth">
        <div v-if="auth.isLoggedIn" class="flex items-center gap-2">
          <slot name="actions" />
          <CoreAppNotifications />
          <CoreHeaderProfile />
        </div>
        <Button v-else size="sm" @click="openLoginModal()">
          Login or Register
        </Button>
      </slot>
    </ClientOnly>
  </div>
</template>
